import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ExButton } from "@energyx-dev/energyx-ui";

export type TFooterProps = {
  deployEnv: "main" | "dev" | "stage" | "local";
};

const corporationInfo = [
  {
    item: "Address",
    info: "2nd floor, 38, 102 Gangnam-daero, Gangnam-gu, Seoul",
  },
  {
    item: "Business License",
    info: "736-86-01306",
  },
  {
    item: "CEO",
    info: "Seonghyun Park Duhwa Hong",
  },
  {
    item: "C/S",
    info: "02-554-9114",
  },
];

const snsLink = [
  {
    sns: "youtube",
    logo: "https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/youtube_logo.png",
    link: "https://www.youtube.com/channel/UCsWfQHJu8LiyPX9emfkjJrA",
  },
  {
    sns: "naver",
    logo: "https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/naver_logo.png",
    link: "https://blog.naver.com/energyx_official",
  },
  {
    sns: "facebook",
    logo: "https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/facebook_logo.png",
    link: "https://www.facebook.com/energyx.official",
  },
  {
    sns: "instagram",
    logo: "https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/instagram_logo.png",
    link: "https://www.instagram.com/energyx_official/",
  },
  {
    sns: "kakaochannel",
    logo: "https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/kakaochannel_logo.png",
    link: "https://pf.kakao.com/_JeRPxb",
  },
];

const createTermsUrl = (deployEnv: "main" | "local" | "stage" | "dev") => {
  switch (deployEnv) {
    case "stage":
      return "https://stage-member.energyx.ai/terms?fetchfrom=ENERGYX&tabValue=";
    case "main":
      return "https://member.energyx.ai/terms?fetchfrom=ENERGYX&tabValue=";
    default:
      return "https://dev-member.energyx.co.kr/terms?fetchfrom=ENERGYX&tabValue=";
  }
};

const EnMainFooter = ({ deployEnv }: TFooterProps) => {
  return (
    <footer
      css={(theme) => css`
        padding: 40px 24px;
        border-top: 1px solid ${theme.color.primary["20"]};
        display: flex;
        justify-content: center;

        ${theme.breakpoints.down("DESKTOP")} {
          padding: 40px 16px;
        }

        ${theme.breakpoints.down("TABLET")} {
          padding: 32px 8px;
        }
      `}
    >
      <div
        css={(theme) => css`
          width: 1440px;

          @media (max-width: 1440px) {
            width: 100%;

            ${theme.breakpoints.down("DESKTOP")} {
              padding: 0 8px;
            }
          }
        `}
      >
        <FooterTopBox>
          <ExButton
            variant="contents"
            size="tiny"
            label="Terms of Use"
            onClick={() => window.open(`${createTermsUrl(deployEnv)}PRIVACY_USE_AGREEMENT`)}
          />
          <ExButton
            variant="contents"
            size="tiny"
            label="Privacy Policy"
            onClick={() => window.open(`${createTermsUrl(deployEnv)}PRIVACY_POLICY`)}
          />
          <ExButton
            variant="contents"
            size="tiny"
            label="Guidelines"
            onClick={() => window.open("https://guide.energyx.ai/")}
          />
          <ExButton
            variant="contents"
            size="tiny"
            label="Careers"
            onClick={() => window.open("https://nrgx.notion.site/e9be444910534fbc9446feac61204332")}
          />
        </FooterTopBox>
        <FooterBottomBox>
          <div>
            {corporationInfo.map((info, index) => (
              <div key={index}>
                <div>{info.item}</div>
                <i />
                <span>{info.info}</span>
              </div>
            ))}
          </div>
          <div>
            <div>
              {snsLink.map((sns, index) => (
                <div key={index} onClick={() => window.open(sns.link)} aria-hidden={true}>
                  <img src={sns.logo} alt="sns로고" />
                </div>
              ))}
            </div>
            <img
              src="https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/energyx-logo.svg"
              alt="에너지엑스 로고"
            />
          </div>
        </FooterBottomBox>
      </div>
    </footer>
  );
};

export default EnMainFooter;

const FooterTopBox = styled.div`
  padding-bottom: 24px;
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  ${({ theme }) => theme.font.Button_s};

  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    padding-bottom: 16px;
    justify-content: flex-start;
    margin-left: -8px;
  }

  > div {
    label {
      color: ${({ theme }) => theme.color.mono["70"]};
    }
  }

  > i {
    width: 1px;
    height: 14px;
    background-color: ${({ theme }) => theme.color.primary["10"]};
  }
`;

const FooterBottomBox = styled.div`
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.color.primary["10"]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;

  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
  }

  > div:first-of-type {
    display: flex;
    gap: 32px;

    ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
      flex-direction: column;
      gap: 8px;
    }

    > div {
      display: flex;
      align-items: flex-start;
      gap: 12px;

      > div {
        ${({ theme }) => theme.font.Caption_h};
        color: ${({ theme }) => theme.color.mono["70"]};
      }

      > i {
        width: 1px;
        height: 14px;
        background-color: ${({ theme }) => theme.color.mono["20"]};

        ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
          background-color: ${({ theme }) => theme.color.mono["10"]};
        }
      }

      > span {
        font-weight: 400;
        font-size: 12px;
        line-height: 18px;
        letter-spacing: 0.003em;
        color: ${({ theme }) => theme.color.mono["70"]};
      }
    }
  }

  > div:nth-of-type(2) {
    display: flex;
    align-items: center;
    gap: 24px;

    ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
      width: 100%;
      align-items: center;
      justify-content: space-between;
    }

    > div {
      display: flex;
      gap: 20px;

      ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
        gap: 8px;
      }
    }

    > img {
      width: 113px;
      height: 17px;
    }
  }
`;
